import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Pinterest as PinterestIcon,
  Email as EmailIcon,
  Article as BlogIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { XIcon } from '../../components/icons/XIcon';
import { TikTokIcon } from '../../components/icons/TikTokIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getPlatformDisplayName } from '../../utils/platformUtils';

interface PlatformsStepProps {
  formData: any;
  allPlatforms: any[];
  availablePlatforms: any[];
  loadingAllPlatforms: boolean;
  loadingAvailablePlatforms: boolean;
  onPlatformChange: (platformId: number, checked: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isValid: boolean;
}

const PlatformsStep: React.FC<PlatformsStepProps> = ({
  formData,
  allPlatforms,
  availablePlatforms,
  loadingAllPlatforms,
  loadingAvailablePlatforms,
  onPlatformChange,
  onNext,
  onBack,
  isValid,
}) => {
  const { t } = useTranslation('profile');
  
  const isSelected = (platformId: number) => {
    return formData.preferred_platform_ids.includes(platformId);
  };

  const isAvailable = (platformId: number) => {
    return availablePlatforms?.some(p => p.id === platformId);
  };

  const sortedPlatforms = allPlatforms?.sort((a, b) => {
    const aAvailable = isAvailable(a.id);
    const bAvailable = isAvailable(b.id);
    
    // Available platforms first
    if (aAvailable && !bAvailable) return -1;
    if (!aAvailable && bAvailable) return 1;
    
    // Then sort by name
    return a.display_name.localeCompare(b.display_name);
  });

  const getPlatformIcon = (platformName: string) => {
    const baseIconProps = { sx: { fontSize: 40 } };
    
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon {...baseIconProps} sx={{ fontSize: 40, color: '#E4405F' }} />;
      case 'facebook':
        return <FacebookIcon {...baseIconProps} sx={{ fontSize: 40, color: '#1877F2' }} />;
      case 'twitter':
      case 'x':
        return <XIcon {...baseIconProps} sx={{ fontSize: 40, color: '#000000' }} />;
      case 'linkedin':
        return <LinkedInIcon {...baseIconProps} sx={{ fontSize: 40, color: '#0A66C2' }} />;
      case 'youtube':
        return <YouTubeIcon {...baseIconProps} sx={{ fontSize: 40, color: '#FF0000' }} />;
      case 'pinterest':
        return <PinterestIcon {...baseIconProps} sx={{ fontSize: 40, color: '#BD081C' }} />;
      case 'email':
      case 'email_marketing':
        return <EmailIcon {...baseIconProps} sx={{ fontSize: 40, color: '#6B7280' }} />;
      case 'blog':
        return <BlogIcon {...baseIconProps} sx={{ fontSize: 40, color: '#10B981' }} />;
      case 'tiktok':
        return <TikTokIcon {...baseIconProps} sx={{ fontSize: 40, color: '#000000' }} />;
      default:
        return <BlogIcon {...baseIconProps} sx={{ fontSize: 40, color: 'primary.main' }} />;
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        {t('businessProfile.platforms.description')}
      </Typography>

      {loadingAllPlatforms || loadingAvailablePlatforms ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <LoadingSpinner message={t('businessProfile.platforms.loadingPlatforms')} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {sortedPlatforms?.map((platform) => {
            const selected = isSelected(platform.id);
            const available = isAvailable(platform.id);
            
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={platform.id}>
                <Card
                  sx={{
                    cursor: available ? 'pointer' : 'not-allowed',
                    opacity: available ? 1 : 0.6,
                    border: selected ? 2 : (available ? 1 : 3),
                    borderColor: selected ? 'primary.main' : (available ? 'divider' : '#D4AF37'),
                    '&:hover': available ? {
                      boxShadow: 2,
                    } : {},
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => available && onPlatformChange(platform.id, !selected)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      {getPlatformIcon(platform.name)}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {getPlatformDisplayName(platform.display_name || platform.name)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {platform.description}
                    </Typography>
                    {selected && (
                      <Chip
                        label={t('businessProfile.platforms.selected')}
                        color="primary"
                        size="small"
                        icon={<CheckIcon />}
                      />
                    )}
                    {!available && (
                      <Chip
                        label={t('businessProfile.platforms.upgradeRequired')}
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
        <Button variant="outlined" onClick={onBack} startIcon={<BackIcon />}>{t('businessProfile.buttons.back')}</Button>
      </Box>
    </Box>
  );
};

export default PlatformsStep;
